import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Avatar,
    Box,
    ButtonBase,
    Card,
    CardActionArea,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    LinearProgress,
    Paper,
    Skeleton,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    alpha,
    useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import TrendingFlatIcon from '@mui/icons-material/TrendingFlat';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { getSchoolDashboard } from '../../common/apiClient';

/**
 * Shared plumbing and tiles for the two school dashboards.
 *
 * MY CLASS and the COMMAND CENTRE differ in scope, not in shape: both read one
 * school's own database, both show a roster, and both carry the same set of
 * not-yet-available widgets. Keeping the pieces here means the two screens are
 * compositions rather than two near-copies that drift apart.
 *
 * Everything visual below draws on the MUI theme rather than fixed colours, so
 * the school dashboards follow the admin panel's theme swapper -- including
 * dark mode -- instead of being a bright island that stops matching the first
 * time somebody changes the palette.
 */

// ---------------------------------------------------------------------------
// Types -- mirror the response of api/School_dashboard.php
// ---------------------------------------------------------------------------

export interface SchoolSummary {
    id: number;
    code: string;
    name: string;
    city?: string | null;
    state?: string | null;
    board?: string | null;
    status: string;
}

export interface AcademicYear {
    id: number;
    name: string;
    starts_on: string;
    ends_on: string;
}

export interface SectionRow {
    id: number;
    label: string;
    class_name: string;
    section_name: string;
    stream: string | null;
    student_count: number;
    class_teacher: string | null;
}

export interface TeacherRow {
    id: number;
    user_id: number;
    full_name: string;
    email: string | null;
    employee_no: string | null;
    section_count: number;
    is_class_teacher: boolean;
}

export interface StudentRow {
    id: number;
    user_id: number;
    full_name: string;
    roll_number: string | null;
    section_label: string;
}

export interface AssignmentRow {
    id: number;
    class_section_id: number;
    section_label: string;
    subject_id: number | null;
    subject: string;
    is_class_teacher: boolean;
}

export interface PendingWidget {
    widget: string;
    available: boolean;
    reason: string;
}

/**
 * One measured tile, as `School_dashboard::filled_widgets()` returns it.
 *
 * The rows are the semantic layer's own output, so the keys are metric names
 * (`accuracy_pct`) and dimension output keys (`user_id`, `week_start`) -- never
 * display labels. Anything that needs a name resolves it against the roster
 * blocks on the same response; see `nameByUserId()`.
 */
export interface WidgetRow {
    [key: string]: number | string | null;
}

export interface FilledWidget {
    widget: string;
    source?: string;
    available: boolean;
    reason?: string;
    label?: string;
    chart?: 'stat' | 'bar' | 'line' | 'table';
    help?: string;
    risk_band?: string | null;
    tier?: string;
    metrics?: string[];
    dimensions?: string[];
    engines?: string[];
    range?: {
        date_from?: string;
        date_to?: string;
        window_days?: number;
    };
    notes?: string[];
    rows?: WidgetRow[];
}

/** `School_dashboard::teacher_adoption()` -- who is setting work, last 30 days. */
export interface TeacherAdoption {
    window_days: number;
    teachers_total: number;
    teachers_active: number;
    assignments_published: number;
    tests_created?: number;
    teachers: AdoptionTeacher[];
}

/** One assignment a teacher published in the adoption window. */
export interface AdoptionAssignment {
    assignment_id: number;
    title: string;
    subject: string | null;
    sections: string | null;
    published_at: string | null;
    scheduled_start: string | null;
    scheduled_end: string | null;
    state: 'open' | 'closed' | 'upcoming';
    assigned: number;
    submitted: number;
    missed: number;
    average_pct: number | null;
}

export interface AdoptionTeacher {
    user_id: number;
    full_name: string | null;
    tests_created: number;
    assignments_published: number;
    last_published_at: string | null;
    /** Sections the teacher is assigned to. */
    sections?: string[];
    /** Over closed assignments in the window; null when none has closed. */
    completion_pct?: number | null;
    average_pct?: number | null;
    assignments?: AdoptionAssignment[];
}

/** `School_dashboard::recent_tests()` -- the teacher's last few closed tests. */
export interface RecentTest {
    assignment_id: number;
    title: string;
    subject: string | null;
    scheduled_end: string;
    assigned: number;
    submitted: number;
    average_pct: number | null;
}

/** `School_dashboard::latest_test()` -- the teacher's most recent closed test. */
export interface LatestTest {
    assignment_id: number;
    title: string;
    subject: string | null;
    scheduled_start: string;
    scheduled_end: string;
    assigned: number;
    submitted: number;
    missed: number;
    average_pct: number | null;
    highest_pct: number | null;
    lowest_pct: number | null;
    sections: Array<{
        section_label: string;
        assigned: number;
        submitted: number;
        missed: number;
        average_pct: number | null;
    }>;
}

export interface PrincipalData {
    audience: 'management';
    school: SchoolSummary | null;
    academic_year: AcademicYear | null;
    roster: {
        students: number;
        teachers: number;
        sections: number;
        avg_students_per_section: number | null;
    };
    sections: SectionRow[];
    teachers: TeacherRow[];
    coverage: {
        sections_without_class_teacher: string[];
        sections_without_students: string[];
        teachers_without_assignment: string[];
    };
    teacher_adoption?: TeacherAdoption;
    students?: StudentRow[];
    widgets?: FilledWidget[];
    pending: PendingWidget[];
}

export interface TeacherData {
    audience: 'teacher';
    school: SchoolSummary | null;
    academic_year: AcademicYear | null;
    teacher: {
        id?: number;
        user_id: number;
        full_name: string | null;
        email?: string | null;
        employee_no?: string | null;
        record_missing?: boolean;
    };
    sections: SectionRow[];
    assignments: AssignmentRow[];
    students: StudentRow[];
    latest_test?: LatestTest | null;
    recent_tests?: RecentTest[];
    widgets?: FilledWidget[];
    pending: PendingWidget[];
}

// ---------------------------------------------------------------------------
// Data loading
// ---------------------------------------------------------------------------

interface DashboardState<T> {
    data: T | null;
    loading: boolean;
    error: string | null;
}

/**
 * Loads one school dashboard.
 *
 * Reports the failure rather than rendering an empty dashboard on error: an
 * empty COMMAND CENTRE and a broken one look identical to a principal, and only
 * one of them is worth calling support about.
 */
export function useSchoolDashboard<T>(audience: 'principal' | 'teacher'): DashboardState<T> {
    const [state, setState] = useState<DashboardState<T>>({
        data: null,
        loading: true,
        error: null,
    });

    useEffect(() => {
        let cancelled = false;

        getSchoolDashboard(audience)
            .then((response: any) => {
                if (cancelled) return;
                if (response && response.status === 'success') {
                    setState({ data: response.result as T, loading: false, error: null });
                } else {
                    setState({
                        data: null,
                        loading: false,
                        error: (response && response.message) || 'Could not load the dashboard.',
                    });
                }
            })
            .catch((err: any) => {
                if (cancelled) return;
                setState({
                    data: null,
                    loading: false,
                    error: err?.message || 'Could not reach the server.',
                });
            });

        return () => {
            cancelled = true;
        };
    }, [audience]);

    return state;
}

// ---------------------------------------------------------------------------
// Reading the measured widgets
// ---------------------------------------------------------------------------

/** One tile out of the `widgets` array, by the tile name the API keys it on. */
export function findWidget(
    widgets: FilledWidget[] | undefined,
    tile: string
): FilledWidget | undefined {
    return (widgets || []).find(w => w.widget === tile);
}

/** A tile that answered, with at least one row. Anything else is not a number. */
export function widgetRows(
    widgets: FilledWidget[] | undefined,
    tile: string
): WidgetRow[] {
    const w = findWidget(widgets, tile);
    return w && w.available && Array.isArray(w.rows) ? w.rows : [];
}

export type BandKey = 'healthy' | 'watch' | 'intervention' | 'nodata';

export interface Band {
    key: BandKey;
    label: string;
    hint: string;
}

export const BANDS: Band[] = [
    { key: 'healthy', label: 'Healthy', hint: '70% accuracy and above' },
    { key: 'watch', label: 'Watch', hint: '50% to 70% accuracy' },
    { key: 'intervention', label: 'Intervention', hint: 'Below 50% accuracy' },
    { key: 'nodata', label: 'Not enough data', hint: 'Fewer than 20 questions answered' },
];

/**
 * The banding thresholds, as a documented default.
 *
 * These are NOT a product decision yet. The mock-up's healthy/watch/
 * intervention split needs per-school boundaries held in the school's own
 * configuration; until that exists the defaults live here, in one place, so
 * there is exactly one thing to change when it does.
 *
 * The floor matters as much as the boundaries. A student who answered four
 * questions has no accuracy worth banding, and putting them in "healthy"
 * because 3/4 is 75% would hide precisely the child a teacher is looking for.
 * They get their own band and are counted separately.
 */
export const BAND_THRESHOLDS = { healthy: 70, watch: 50, minAnswered: 20 };

export function bandOf(row: WidgetRow): BandKey {
    const answered = Number(row.questions_attempted ?? 0);
    const accuracy = row.accuracy_pct;

    if (accuracy === null || accuracy === undefined || answered < BAND_THRESHOLDS.minAnswered) {
        return 'nodata';
    }
    const pct = Number(accuracy);
    if (pct >= BAND_THRESHOLDS.healthy) return 'healthy';
    if (pct >= BAND_THRESHOLDS.watch) return 'watch';
    return 'intervention';
}

export function bandCounts(rows: WidgetRow[]): Record<BandKey, number> {
    const out: Record<BandKey, number> = { healthy: 0, watch: 0, intervention: 0, nodata: 0 };
    rows.forEach(r => {
        out[bandOf(r)] += 1;
    });
    return out;
}

/** Roster lookup, so a metric row keyed on `user_id` can be shown as a name. */
export function nameByUserId(people: Array<{ user_id: number; full_name: string | null }>) {
    const map = new Map<number, string>();
    people.forEach(p => {
        if (p.full_name) map.set(Number(p.user_id), p.full_name);
    });
    return map;
}

// ---------------------------------------------------------------------------
// Visual primitives
// ---------------------------------------------------------------------------

export const BAND_COLOR: Record<BandKey, 'success' | 'warning' | 'error' | 'info'> = {
    healthy: 'success',
    watch: 'warning',
    intervention: 'error',
    nodata: 'info',
};

/**
 * The banner every school dashboard opens on.
 *
 * A tinted panel rather than a plain white strip, because this is the one place
 * that answers "whose school am I looking at, and which year" -- the two
 * questions that make every number below it mean something.
 */
export const DashboardHeader = ({
    title,
    subtitle,
    school,
    year,
    icon,
    children,
}: {
    title: string;
    subtitle: string;
    school: SchoolSummary | null;
    year: AcademicYear | null;
    icon?: React.ReactNode;
    children?: React.ReactNode;
}) => {
    const theme = useTheme();

    return (
        <Paper
            elevation={0}
            sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 2.5, md: 3.5 },
                mb: 2.5,
                borderRadius: 3,
                color: theme.palette.primary.contrastText,
                background: `linear-gradient(120deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 55%, ${alpha(
                    theme.palette.secondary.main,
                    0.85
                )} 100%)`,
                // A soft highlight so the panel reads as a surface rather than
                // a flat block of brand colour.
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: -120,
                    right: -80,
                    width: 320,
                    height: 320,
                    borderRadius: '50%',
                    background: alpha('#fff', 0.09),
                    pointerEvents: 'none',
                },
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={2}
                sx={{ position: 'relative', zIndex: 1 }}
            >
                <Stack direction="row" spacing={2} alignItems="center">
                    {icon && (
                        <Avatar
                            sx={{
                                width: 52,
                                height: 52,
                                bgcolor: alpha('#fff', 0.18),
                                color: 'inherit',
                                backdropFilter: 'blur(2px)',
                            }}
                        >
                            {icon}
                        </Avatar>
                    )}
                    <Box>
                        <Typography
                            variant="overline"
                            sx={{ opacity: 0.85, letterSpacing: 1.4, fontWeight: 600 }}
                        >
                            {subtitle}
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.15 }}>
                            {title}
                        </Typography>
                        {school && (
                            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                                {school.name}
                                {school.city ? ` · ${school.city}` : ''}
                                {school.board ? ` · ${school.board}` : ''}
                            </Typography>
                        )}
                    </Box>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    {children}
                    {school && (
                        <Chip
                            size="small"
                            label={school.code}
                            sx={{
                                bgcolor: alpha('#fff', 0.16),
                                color: 'inherit',
                                fontWeight: 600,
                            }}
                        />
                    )}
                    {year ? (
                        <Chip
                            size="small"
                            label={`AY ${year.name}`}
                            sx={{ bgcolor: alpha('#fff', 0.9), color: theme.palette.primary.dark, fontWeight: 700 }}
                        />
                    ) : (
                        <Chip size="small" color="warning" label="No current academic year" />
                    )}
                </Stack>
            </Stack>
        </Paper>
    );
};

export type Tone = 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';

/**
 * A headline number.
 *
 * `delta` is deliberately a string plus a direction rather than a number: a
 * change of zero and a change we could not compute must not look the same, and
 * "not enough data" has to be sayable in the same slot as "+6".
 */
export const StatCard = ({
    label,
    value,
    hint,
    icon,
    tone = 'primary',
    delta,
    deltaDirection,
    progress,
    onClick,
    actionLabel,
}: {
    label: string;
    value: React.ReactNode;
    hint?: string;
    icon?: React.ReactNode;
    tone?: Tone;
    delta?: string;
    deltaDirection?: 'up' | 'down' | 'flat';
    progress?: number | null;
    /** Makes the whole card a button -- to the screen or list behind the number. */
    onClick?: () => void;
    /** Says where the click goes, e.g. "Open students". Shown on the card. */
    actionLabel?: string;
}) => {
    const theme = useTheme();
    const color = theme.palette[tone].main;

    const DeltaIcon =
        deltaDirection === 'up'
            ? ArrowUpwardIcon
            : deltaDirection === 'down'
            ? ArrowDownwardIcon
            : TrendingFlatIcon;

    return (
        <Card
            elevation={0}
            sx={{
                flex: '1 1 200px',
                minWidth: 200,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                position: 'relative',
                overflow: 'hidden',
                transition: 'transform .18s ease, box-shadow .18s ease',
                '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[4] },
                // The accent edge carries the tone, so the number itself can
                // stay in the normal text colour and remain easy to read.
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: 4,
                    bgcolor: color,
                },
            }}
        >
            <CardActionArea
                disabled={!onClick}
                onClick={onClick}
                aria-label={actionLabel ? `${label}: ${actionLabel}` : undefined}
                sx={{ height: '100%', '&.Mui-disabled': { opacity: 1 } }}
            >
            <CardContent sx={{ pl: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {label}
                    </Typography>
                    {icon && (
                        <Avatar
                            variant="rounded"
                            sx={{
                                width: 34,
                                height: 34,
                                bgcolor: alpha(color, 0.12),
                                color,
                            }}
                        >
                            {icon}
                        </Avatar>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mt: 0.5 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                        {value}
                    </Typography>
                    {delta && (
                        <Chip
                            size="small"
                            icon={<DeltaIcon sx={{ fontSize: 14 }} />}
                            label={delta}
                            sx={{
                                height: 22,
                                fontWeight: 600,
                                bgcolor: alpha(
                                    deltaDirection === 'down'
                                        ? theme.palette.error.main
                                        : deltaDirection === 'up'
                                        ? theme.palette.success.main
                                        : theme.palette.text.secondary,
                                    0.12
                                ),
                                color:
                                    deltaDirection === 'down'
                                        ? theme.palette.error.main
                                        : deltaDirection === 'up'
                                        ? theme.palette.success.main
                                        : theme.palette.text.secondary,
                                '& .MuiChip-icon': { color: 'inherit' },
                            }}
                        />
                    )}
                </Stack>

                {typeof progress === 'number' && (
                    <LinearProgress
                        variant="determinate"
                        value={Math.max(0, Math.min(100, progress))}
                        sx={{
                            mt: 1,
                            height: 6,
                            borderRadius: 3,
                            bgcolor: alpha(color, 0.14),
                            '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
                        }}
                    />
                )}

                {hint && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        {hint}
                    </Typography>
                )}

                {onClick && actionLabel && (
                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mt: 1, color }}>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            {actionLabel}
                        </Typography>
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                    </Stack>
                )}
            </CardContent>
            </CardActionArea>
        </Card>
    );
};

/**
 * The card every block on these screens sits in.
 *
 * One component rather than a repeated Card / CardContent / Typography h6, so
 * the two dashboards cannot drift into two slightly different card headers.
 */
export const Panel = ({
    title,
    subtitle,
    icon,
    tone = 'primary',
    action,
    children,
    sx,
}: {
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    tone?: Tone;
    action?: React.ReactNode;
    children: React.ReactNode;
    sx?: object;
}) => {
    const theme = useTheme();
    const color = theme.palette[tone].main;

    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                height: '100%',
                ...sx,
            }}
        >
            <CardContent>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: subtitle ? 0.25 : 1.5 }}
                >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                        {icon && (
                            <Avatar
                                variant="rounded"
                                sx={{ width: 34, height: 34, bgcolor: alpha(color, 0.12), color }}
                            >
                                {icon}
                            </Avatar>
                        )}
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                            {title}
                        </Typography>
                    </Stack>
                    {action}
                </Stack>

                {subtitle && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                        {subtitle}
                    </Typography>
                )}

                {children}
            </CardContent>
        </Card>
    );
};

/**
 * The quick-actions bar.
 *
 * The first row of a dashboard, because the first question a teacher has on
 * opening it is not "how is my class doing" but "what do I do next" -- and
 * every answer to that was previously three clicks away in the left menu.
 *
 * An action that has no feature behind it is rendered disabled with the
 * reason, never hidden and never wired to a dead route. That is the same
 * honesty the `pending` widgets use: a greyed tile with a reason tells a
 * teacher the platform knows the gap, where a missing button tells them
 * nothing and a live button that goes nowhere is a bug report.
 */
export interface QuickAction {
    label: string;
    hint: string;
    icon: React.ReactNode;
    tone?: Tone;
    /** Route to push. Ignored when `onClick` is given. */
    to?: string;
    onClick?: () => void;
    /** Why this action cannot be used yet. Present means disabled. */
    unavailable?: string;
}

export const QuickActions = ({ actions }: { actions: QuickAction[] }) => {
    const theme = useTheme();
    const navigate = useNavigate();

    return (
        <Paper
            elevation={0}
            sx={{
                p: 1.25,
                mb: 2.5,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
            }}
        >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                {actions.map(action => {
                    const tone = theme.palette[action.tone || 'primary'].main;
                    const disabled = Boolean(action.unavailable);

                    const button = (
                        <Box
                            component="button"
                            type="button"
                            disabled={disabled}
                            onClick={() => {
                                if (disabled) return;
                                if (action.onClick) action.onClick();
                                else if (action.to) navigate(action.to);
                            }}
                            sx={{
                                flex: '1 1 200px',
                                minWidth: 180,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                textAlign: 'left',
                                font: 'inherit',
                                px: 1.5,
                                py: 1.25,
                                borderRadius: 2.5,
                                cursor: disabled ? 'default' : 'pointer',
                                opacity: disabled ? 0.55 : 1,
                                border: `1px solid ${alpha(tone, disabled ? 0.15 : 0.3)}`,
                                bgcolor: theme.palette.background.paper,
                                transition: 'transform .15s ease, box-shadow .15s ease, background-color .15s ease',
                                '&:hover': disabled
                                    ? {}
                                    : {
                                          transform: 'translateY(-2px)',
                                          bgcolor: alpha(tone, 0.06),
                                          boxShadow: `0 6px 16px ${alpha(tone, 0.22)}`,
                                      },
                            }}
                        >
                            <Avatar
                                variant="rounded"
                                sx={{ width: 36, height: 36, bgcolor: alpha(tone, 0.14), color: tone }}
                            >
                                {action.icon}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                    {action.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                    {action.unavailable || action.hint}
                                </Typography>
                            </Box>
                        </Box>
                    );

                    return (
                        <Tooltip key={action.label} title={action.unavailable || action.hint}>
                            <Box sx={{ flex: '1 1 200px', minWidth: 180, display: 'flex' }}>{button}</Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Paper>
    );
};

export const EmptyNote = ({ children }: { children: React.ReactNode }) => (
    <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
        {children}
    </Typography>
);

/**
 * A trend, drawn by hand.
 *
 * Hand-rolled SVG rather than a charting dependency: this is one polyline, and
 * the admin bundle does not need another library to draw it. Gaps are real --
 * a week with no denominator comes back NULL and is skipped, not drawn as
 * zero, which would invent a collapse in accuracy that never happened.
 */
export const Sparkline = ({
    points,
    height = 64,
    tone = 'primary',
}: {
    points: Array<number | null>;
    height?: number;
    tone?: Tone;
}) => {
    const theme = useTheme();
    const color = theme.palette[tone].main;
    const width = 100; // viewBox units; the SVG scales to its container

    const drawn = useMemo(() => {
        const valid = points
            .map((p, i) => ({ p, i }))
            .filter(d => d.p !== null && d.p !== undefined) as Array<{ p: number; i: number }>;

        if (valid.length < 2) return null;

        const values = valid.map(d => d.p);
        const min = Math.min(...values);
        const max = Math.max(...values);
        const span = max - min || 1;
        const stepX = width / Math.max(1, points.length - 1);

        const coords = valid.map(d => ({
            x: d.i * stepX,
            y: height - 6 - ((d.p - min) / span) * (height - 14),
        }));

        return {
            line: coords.map(c => `${c.x.toFixed(2)},${c.y.toFixed(2)}`).join(' '),
            area:
                `${coords[0].x.toFixed(2)},${height} ` +
                coords.map(c => `${c.x.toFixed(2)},${c.y.toFixed(2)}`).join(' ') +
                ` ${coords[coords.length - 1].x.toFixed(2)},${height}`,
            last: coords[coords.length - 1],
        };
    }, [points, height]);

    if (!drawn) {
        return <EmptyNote>Not enough weeks yet to draw a trend.</EmptyNote>;
    }

    return (
        <Box sx={{ width: '100%' }}>
            <svg
                viewBox={`0 0 ${width} ${height}`}
                preserveAspectRatio="none"
                width="100%"
                height={height}
                role="img"
                aria-label="Trend"
            >
                <polygon points={drawn.area} fill={alpha(color, 0.14)} />
                <polyline
                    points={drawn.line}
                    fill="none"
                    stroke={color}
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                />
                <circle cx={drawn.last.x} cy={drawn.last.y} r={2.5} fill={color} />
            </svg>
        </Box>
    );
};

/**
 * Healthy / watch / intervention as one bar plus its legend.
 *
 * A stacked bar rather than three separate numbers, because the question a
 * teacher actually has is a proportion: how much of my class is in trouble.
 */
/**
 * `onSelect`, when given, makes each segment and each count clickable -- the
 * way into the list of students behind a number. Without it the bar is the
 * plain summary it always was.
 */
export const BandBar = ({
    counts,
    onSelect,
}: {
    counts: Record<BandKey, number>;
    onSelect?: (band: BandKey) => void;
}) => {
    const theme = useTheme();
    const total = BANDS.reduce((sum, b) => sum + counts[b.key], 0);
    const clickable = (b: Band) => Boolean(onSelect) && counts[b.key] > 0;

    if (total === 0) {
        return <EmptyNote>No student has answered anything in this window yet.</EmptyNote>;
    }

    return (
        <Box>
            <Stack
                direction="row"
                sx={{ height: 14, borderRadius: 7, overflow: 'hidden', mb: 2 }}
            >
                {BANDS.filter(b => counts[b.key] > 0).map(b => (
                    <Tooltip
                        key={b.key}
                        title={`${b.label}: ${counts[b.key]} — ${b.hint}${onSelect ? ' · click to see who' : ''}`}
                    >
                        <Box
                            onClick={onSelect ? () => onSelect(b.key) : undefined}
                            sx={{
                                width: `${(counts[b.key] / total) * 100}%`,
                                bgcolor: theme.palette[BAND_COLOR[b.key]].main,
                                opacity: b.key === 'nodata' ? 0.35 : 1,
                                cursor: onSelect ? 'pointer' : 'default',
                                transition: 'filter .15s',
                                '&:hover': onSelect ? { filter: 'brightness(0.9)' } : {},
                            }}
                        />
                    </Tooltip>
                ))}
            </Stack>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {BANDS.map(b => (
                    <ButtonBase
                        key={b.key}
                        disabled={!clickable(b)}
                        onClick={() => onSelect && onSelect(b.key)}
                        sx={{
                            flex: '1 1 110px',
                            minWidth: 110,
                            display: 'block',
                            textAlign: 'left',
                            borderRadius: 2,
                            p: 0.75,
                            m: -0.75,
                            '&:hover': clickable(b)
                                ? { bgcolor: alpha(theme.palette[BAND_COLOR[b.key]].main, 0.08) }
                                : {},
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                                sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: theme.palette[BAND_COLOR[b.key]].main,
                                    opacity: b.key === 'nodata' ? 0.35 : 1,
                                }}
                            />
                            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
                                {counts[b.key]}
                            </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {b.label}
                        </Typography>
                    </ButtonBase>
                ))}
            </Box>
        </Box>
    );
};

/** Initials, for the roster avatars. Two letters is enough to scan a list by. */
export const initialsOf = (name: string | null) =>
    (name || '?')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(p => p[0])
        .join('')
        .toUpperCase();

export const SectionTable = ({
    sections,
    emptyMessage,
}: {
    sections: SectionRow[];
    emptyMessage: string;
}) => {
    const theme = useTheme();

    if (sections.length === 0) {
        return <EmptyNote>{emptyMessage}</EmptyNote>;
    }

    // Relative fill, so a section's size is readable at a glance rather than
    // being a number the eye has to compare against every other row.
    const biggest = Math.max(...sections.map(s => s.student_count), 1);

    return (
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Stream</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 160 }}>Students</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Class teacher</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {sections.map(s => (
                    <TableRow
                        key={s.id}
                        hover
                        sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                        <TableCell>
                            <Chip
                                size="small"
                                label={s.label}
                                sx={{
                                    fontWeight: 700,
                                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                }}
                            />
                        </TableCell>
                        <TableCell>{s.stream || '—'}</TableCell>
                        <TableCell>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 28 }}>
                                    {s.student_count}
                                </Typography>
                                <LinearProgress
                                    variant="determinate"
                                    value={(s.student_count / biggest) * 100}
                                    sx={{
                                        flex: 1,
                                        height: 6,
                                        borderRadius: 3,
                                        bgcolor: alpha(theme.palette.primary.main, 0.12),
                                        '& .MuiLinearProgress-bar': { borderRadius: 3 },
                                    }}
                                />
                            </Stack>
                        </TableCell>
                        <TableCell>
                            {s.class_teacher || (
                                <Chip size="small" color="warning" variant="outlined" label="Unassigned" />
                            )}
                        </TableCell>
                    </TableRow>
                ))}
            </TableBody>
        </Table>
    );
};

const WIDGET_TITLES: Record<string, string> = {
    academic_impact: 'Academic impact',
    engagement: 'Engagement',
    intervention_counts: 'Interventions',
    school_wide_gaps: 'School-wide gaps',
    device_health: 'Device health',
    performance_bands: 'Healthy / watch / intervention',
    students_needing_attention: 'Students needing attention',
    class_performance_by_subject: 'Performance by subject',
    top_weak_areas: 'Top weak areas',
};

/**
 * The specced tiles that have no data source yet.
 *
 * These render as visibly pending, each stating what it is waiting on. The
 * alternative -- showing 0% accuracy or an empty chart -- would be read as a
 * measurement rather than as an absence, and a school looking at a fabricated
 * zero has no way to tell it apart from a real one.
 */
export const PendingWidgets = ({ pending }: { pending: PendingWidget[] }) => {
    const theme = useTheme();
    if (!pending || pending.length === 0) return null;

    return (
        <Panel
            title="Coming with learning data"
            tone="info"
            icon={<InfoOutlinedIcon fontSize="small" />}
            subtitle="Shown as pending rather than as zero — an empty chart would read as a result."
            action={
                <Tooltip title="These tiles read the daily roll-up tables, which are built once school quiz attempts start landing in this school's database.">
                    <InfoOutlinedIcon fontSize="small" color="disabled" />
                </Tooltip>
            }
        >
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                {pending.map(w => (
                    <Paper
                        key={w.widget}
                        variant="outlined"
                        sx={{
                            flex: '1 1 260px',
                            minWidth: 260,
                            p: 2,
                            borderRadius: 2,
                            borderStyle: 'dashed',
                            bgcolor: alpha(theme.palette.info.main, 0.04),
                        }}
                    >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                            <Skeleton variant="rounded" width={26} height={26} animation={false} />
                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                {WIDGET_TITLES[w.widget] || w.widget}
                            </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {w.reason}
                        </Typography>
                    </Paper>
                ))}
            </Box>
        </Panel>
    );
};

export const DashboardLoading = () => (
    <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
    </Box>
);

export const DashboardError = ({ message }: { message: string }) => (
    <Alert severity="error" sx={{ m: 2, borderRadius: 2 }}>
        {message}
    </Alert>
);
